import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccounts, createAccount, updateAccount, deactivateAccount } from '../../../api/gl';
import { useAuth } from '../../../contexts/AuthContext';
import Pagination from '../../../components/common/Pagination';

const WholesaleChartOfAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const accountTypes = [
    { value: 'asset', label: 'Assets', color: 'bg-blue-100 text-blue-800' },
    { value: 'liability', label: 'Liabilities', color: 'bg-red-100 text-red-800' },
    { value: 'equity', label: 'Equity', color: 'bg-purple-100 text-purple-800' },
    { value: 'revenue', label: 'Revenue', color: 'bg-green-100 text-green-800' },
    { value: 'cogs', label: 'Cost of Goods Sold', color: 'bg-orange-100 text-orange-800' },
    { value: 'expense', label: 'Expenses', color: 'bg-yellow-100 text-yellow-800' }
  ];

  useEffect(() => {
    fetchAccounts();
  }, [filterType, searchTerm]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        module: 'wholesale' // Filter by wholesale module
      };
      if (filterType) params.account_type = filterType;
      if (searchTerm) params.search = searchTerm;

      const response = await getAccounts(params);

      const accountsData = response?.data || [];
      setAccounts(accountsData);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch wholesale accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAccount(null);
    setShowModal(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const handleDeactivate = async (account) => {
    if (!window.confirm(`Are you sure you want to deactivate wholesale account "${account.account_name}"?`)) {
      return;
    }

    try {
      await deactivateAccount(account.id);
      fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to deactivate account');
    }
  };

  const getTypeColor = (type) => {
    const found = accountTypes.find(t => t.value === type);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.account_type;
    if (!groups[type]) groups[type] = [];
    groups[type].push(account);
    return groups;
  }, {});

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'wholesale_admin' || user?.role === 'system_admin';

  const totalPages = Math.ceil(accounts.length / itemsPerPage);
  const paginatedAccounts = accounts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wholesale Chart of Accounts</h1>
          <p className="text-sm text-gray-500">Manage wholesale General Ledger accounts</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm transition-all"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            <span>+ Add Account</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by code or name..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              <option value="">All Types</option>
              {accountTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-600 hover:text-gray-900'}`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'tree' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Tree
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {accountTypes.map(type => (
          <div key={type.value} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 hover:border-emerald-200 transition-all">
            <div className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${type.color} mb-2`}>
              {type.label}
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {accounts.filter(a => a.account_type === type.value).length}
            </div>
          </div>
        ))}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--wholesale-primary, #047857)' }}></div>
        </div>
      ) : (
        <>
          {/* Accounts Table */}
          {viewMode === 'list' ? (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Normal Balance</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      {isAdmin && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedAccounts.map(account => (
                      <tr key={account.id} className={`hover:bg-gray-50 ${account.is_header_account ? 'bg-gray-50/50 font-semibold' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-emerald-700">{account.account_code}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {account.parent_account_id && <span className="text-gray-400 mr-2">└</span>}
                          {account.account_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase rounded ${getTypeColor(account.account_type)}`}>
                            {account.account_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{account.normal_balance}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                          <span className={parseFloat(account.current_balance) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                            {formatCurrency(account.current_balance)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                            account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {account.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button onClick={() => handleEdit(account)} className="text-emerald-600 hover:text-emerald-900">Edit</button>
                              {!account.is_system_account && (
                                <button onClick={() => handleDeactivate(account)} className="text-red-600 hover:text-red-900">
                                  {account.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {accounts.length === 0 && (
                <div className="text-center py-12 text-gray-500">No wholesale accounts found</div>
              )}

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={accounts.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                itemLabel="accounts"
              />
            </div>
          ) : (
            // Tree View
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
              {accountTypes.map(type => {
                const typeAccounts = groupedAccounts[type.value] || [];
                if (typeAccounts.length === 0) return null;

                return (
                  <div key={type.value} className="mb-6">
                    <h3 className={`inline-block px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider mb-3 ${type.color}`}>
                      {type.label} ({typeAccounts.length})
                    </h3>
                    <div className="ml-4 space-y-1 border-l-2 border-gray-50 pl-4">
                      {typeAccounts.map(account => (
                        <div
                          key={account.id}
                          className={`flex justify-between items-center p-2 rounded hover:bg-gray-50 ${
                            account.is_header_account ? 'font-semibold text-gray-900' : 'text-gray-600'
                          }`}
                          style={{ marginLeft: `${((account.level || 1) - 1) * 20}px` }}
                        >
                          <div className="text-sm">
                            <span className="font-mono text-emerald-700 mr-2">{account.account_code}</span>
                            <span>{account.account_name}</span>
                          </div>
                          <span className={`text-sm font-medium ${parseFloat(account.current_balance) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(account.current_balance)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {accounts.length === 0 && (
                <div className="text-center py-12 text-gray-500">No wholesale accounts found</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Account Modal */}
      {showModal && (
        <AccountModal
          account={editingAccount}
          accounts={accounts}
          onClose={() => setShowModal(false)}
          onSave={fetchAccounts}
        />
      )}
    </div>
  );
};

// Account Modal Component
const AccountModal = ({ account, accounts, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    account_code: account?.account_code || '',
    account_name: account?.account_name || '',
    account_type: account?.account_type || 'asset',
    account_category: account?.account_category || '',
    parent_account_id: account?.parent_account_id || '',
    description: account?.description || '',
    is_active: account?.is_active !== false,
    is_header_account: account?.is_header_account || false,
    module: 'wholesale'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (account) {
        await updateAccount(account.id, formData);
      } else {
        await createAccount(formData);
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            {account ? 'Edit Wholesale Account' : 'Create New Wholesale Account'}
          </h2>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Code *</label>
                <input
                  type="text"
                  value={formData.account_code}
                  onChange={(e) => setFormData({ ...formData, account_code: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  disabled={account?.is_system_account}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label>
                <select
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  disabled={account?.is_system_account}
                >
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                  <option value="equity">Equity</option>
                  <option value="revenue">Revenue</option>
                  <option value="cogs">Cost of Goods Sold</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
              <input
                type="text"
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Account</label>
              <select
                value={formData.parent_account_id}
                onChange={(e) => setFormData({ ...formData, parent_account_id: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">None (Top Level)</option>
                {accounts
                  .filter(a => a.account_type === formData.account_type && a.id !== account?.id)
                  .map(a => (
                    <option key={a.id} value={a.id}>
                      {a.account_code} - {a.account_name}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500"
                rows={2}
              />
            </div>

            <div className="flex gap-6 pt-2">
              <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2 h-4 w-4 text-emerald-600 rounded"
                />
                Active
              </label>
              <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_header_account}
                  onChange={(e) => setFormData({ ...formData, is_header_account: e.target.checked })}
                  className="mr-2 h-4 w-4 text-emerald-600 rounded"
                />
                Header Account
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 text-white rounded-lg transition-all shadow-sm disabled:opacity-50"
                style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
              >
                {loading ? 'Saving...' : 'Save Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WholesaleChartOfAccountsPage;
